import { useState, useEffect, useMemo } from 'react';
import bonusData from '../../data/bonus.json';
import articleData from "../../data/financeFacts.json";
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FactsSection from "../../components/FactsSection";
import AdComponent from "../../components/AdComponent";

const Bonus = () => {
    const user = useSelector((state) => state.user.user);
    
    // Fisher-Yates shuffle function for true randomization
    const shuffleArray = (array) => {
        const shuffled = [...array]; // Create a copy to avoid mutating original
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const article = articleData?.PAGE1 || {};

    // Memoize the questions so they don't change on re-renders
    const questions = useMemo(() => {
        const bonusQuestions = bonusData.filter((question) => question.is_bonus === '1');
        return shuffleArray(bonusQuestions).slice(0, 2);
    }, []); // Empty dependency array means this only runs once

    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [startTime] = useState(Date.now());
    const [timer, setTimer] = useState("00:00");
    const [completed, setCompleted] = useState(false);
    const navigate = useNavigate();

    const totalQuestions = questions.length;
    
    useEffect(() => {
        if(user.first_visit === 0) {
            navigate("/");
        }
    }, [user, navigate]);

    // Timer
    useEffect(() => {
        const interval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const minutes = Math.floor(elapsed / 60);
            const seconds = elapsed % 60;
            setTimer(
                `${minutes.toString().padStart(2, "0")}:${seconds
                    .toString()
                    .padStart(2, "0")}`
            );
        }, 1000);

        return () => clearInterval(interval);
    }, [startTime]);

    // Handle option click
    const handleOptionClick = (questionIndex, key, option) => {
        const question = questions[questionIndex];
        const isCorrect = key === question.correct_answer;

        setAnswers((prev) => {
            const updatedAnswers = {
                ...prev,
                [questionIndex]: { value: key, text: option, isCorrect },
            };
        
            setTimeout(() => {
                 if (questionIndex < totalQuestions - 1) {
                    setCurrentIndex(questionIndex + 1);
                } else {
                    if (Object.keys(updatedAnswers).length === totalQuestions) {
                        showSuccessAndSubmit(updatedAnswers);
                    } else {
                        alert("Please answer all questions.");
                    }
                }
            }, 800);
        
            return updatedAnswers;
        });
    };

    const showSuccessAndSubmit = (finalAnswers) => {
        setCompleted(true);
        setTimeout(() => {
            handleSubmit(finalAnswers);
        }, 1000);
    };

    // Handle form submit
    const handleSubmit = (finalAnswers) => {
        if (Object.keys(finalAnswers).length !== totalQuestions) {
            alert("Please answer all questions.");
            return;
        }
        console.log("Submitting answers:", finalAnswers);

        let results = {
            total_questions: questions.length,
            correct_answers: 0,
            wrong_answers: 0,
            score: 0,
            total_points: 0,
            detailed_results: [],
        };
    
        questions.forEach((question, index) => {
            const user_answer = finalAnswers[index] !== undefined ? finalAnswers[index]?.value : null;
            const is_correct = user_answer === question.correct_answer;
            const points = is_correct ? parseInt(question.points) : 0;
        
            results.detailed_results.push({
                question_id: question.id,
                question: question.question,
                user_answer,
                correct_answer: question.correct_answer,
                is_correct,
                points,
                explanation: question.explanation,
            });
        
            if (is_correct) {
                results.correct_answers++;
            } else {
                results.wrong_answers++;
            }
        
            results.score += points;
            results.total_points += parseInt(question.points);
        });
    
        results.percentage =
        results.total_points > 0
            ? Math.round((results.score / results.total_points) * 10000) / 100
            : 0;
    
        console.log("Quiz Results:", results);
    
        navigate("/quiz/bonus/result", { state: results });
    };

    return (
        <>
            <section className="quiz-start-section pt-3">
                <div className="container">
                    <AdComponent />
                    
                    <div className="start-quiz">
                        <div className="quiz-header">
                            <h2 className="quiz-title">Quick Start!</h2>
                            <p className="quiz-subtitle">Answer {totalQuestions} questions and win up to 200 coins.</p>
                        </div>
        
                        <form id="bonus-quiz-form">
                            <input type="hidden" name="start_time" id="start_time" value={`${Date.now()}`} />
                                <div id="quizQuestions">
                                    {questions.map((question, index) => (
                                        <div className={`quiz-question ${currentIndex === index ? 'active' : ''}`} data-question={index} key={index}>
                                            <div className="quiz-progress">
                                                <span>{index + 1}/ <strong> {totalQuestions} </strong>  Question</span>
                                            </div>
                                            
                                            <div className="question-container">
                                                <h3 className="question-text" dangerouslySetInnerHTML={{ __html: question.question }}></h3>
                                                
                                                <div className="quiz-options">
                                                    {(() => {
                                                        const options = {
                                                            a: question.option_a,
                                                            b: question.option_b,
                                                            c: question.option_c,
                                                            d: question.option_d,
                                                        };

                                                        return Object.entries(options).map(([key, option]) => {
                                                            const userAnswer = answers[currentIndex];
                                                            let buttonClass = "";
                                                        
                                                            if (userAnswer) {
                                                                if (userAnswer.value === key) {
                                                                    // User clicked this option
                                                                    buttonClass = userAnswer.isCorrect ? "correct" : "in-correct";
                                                                } else if (!userAnswer.isCorrect && key === question.correct_answer) {
                                                                    // Show correct option if user was wrong
                                                                    buttonClass = "correct";
                                                                }
                                                            }

                                                            return (
                                                                <button
                                                                    key={key}
                                                                    type="button"
                                                                    className={`quiz-option ${buttonClass}`}
                                                                    data-question={index}
                                                                    data-option={key}
                                                                    data-text={option}
                                                                    onClick={() =>
                                                                        handleOptionClick(currentIndex, key, question[`option_${key}`])
                                                                    }
                                                                    disabled={answers[currentIndex]}
                                                                >
                                                                    {/* <span className="option-letter">{key.toUpperCase()}.</span> */}
                                                                    <span className="option-text" dangerouslySetInnerHTML={{ __html: option }} />
                                                                    <i className="fas fa-check option-check"></i>
                                                                </button>
                                                            )
                                                        });
                                                    })()}

                                                    <input type="hidden" name={`answers[${index}]`} id={`answer_${index}`} />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                        </form>

                        <div className="quiz-highlight">
                            <h4>#Highlights</h4>
                            <p>Complete all questions correctly to maximize your bonus coins earning potential.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="common-box-section pt pb">
                <div className="container">
                    <div className="common-box quiz-box">
                        <h2 className="common-title">Participate in quizzes and earn coins!</h2> 
                        <ul className="common-content">
                            <li>The completion of each quiz fetches you coins.</li>
                            <li>Join millions of quiz fans who rely on us.</li>
                            <li>Improve your knowledge with our free, quality quizzes.</li>
                            <li>Compete against a wide variety of players from across the world.</li> 
                            <li>Discover popular categories like Term Life Insurance, Stock Exchange, Insurance, and Loans &amp; Cards!</li>
                        </ul> 
                    </div>
                </div>
            </section>

            <section className="facts-section">
                <div className="container">
                    <div className="finance-fact">
                        <h2 className="fw-bold mb-4">{article?.title}</h2>
                    </div>
                    {
                        article?.content?.map((item, index) => (
                            <FactsSection key={index} item={item} />
                        ))
                    }
                    <div className="article-card">
                        <h2 className="article-card-title">{article?.action?.title}</h2>
                        
                        <p className="article-card-desc">
                            <strong className="desc-color">{article?.action?.title1}</strong> {article?.action?.desc1}
                        </p>
                        
                        <p className="article-card-desc">
                            <strong className="desc-color">{article?.action?.title2}</strong> {article?.action?.desc2}
                        </p>
                        
                        <p className="article-card-desc">
                            {
                                article?.action?.list.map((list, index) => (
                                    <>{list}<br /></>
                                ))
                            }
                        </p>
                    </div>
                </div>
            </section>
        </>
    )
}

export default Bonus;